<template>
  <Card
    :loading="loading && !chartData"
    class="p-4 overflow-hidden relative pie-chart"
  >

    <div class="flex items-center border-b-2 border-black pb-3">
      <div class="font-normal flex-1">
        <slot></slot>
      </div>
      <ExportChart
        class="ml-3"
        @export="handleExport"
      />
    </div>

    <div v-if="loading" class="flex items-center justify-center" style="height:200px;">
      <Loader class="text-60" />
    </div>
    <div v-show="!loading" ref="chartWithLegend" class="flex items-center pt-3">
      <div
        ref="chart"
        class="chart pie clickable w-1/2 mr-3"
        style="height: 160px;"
        @click="handleChartClick"
      ></div>

      <div v-if="chartData" class="w-1/2 pr-6">
        <ul>
          <li
            v-for="(item, index) in chartData.series"
            :key="index"
            class="flex items-center cursor-pointer"
            @click="$emit('rowClick', item.meta)"
          >
            <div class="w-full">
              <div class="w-6 h-6 inline-block rounded align-middle my-1 mr-2"
                   :class="['bg-analytics-'+item.className]"
              ></div>
              {{ item.label }}
            </div>
            <div class="w-16 text-right">
              {{ $n(item.value, 'analyticsInteger') }}
            </div>
          </li>

        </ul>
      </div>

    </div>

  </Card>
</template>

<script>
  import Chartist from 'chartist';
  import domtoimage from 'dom-to-image';
  import Card from '@/_legacy/components/Card.vue';
  import 'chartist/dist/chartist.min.css';
  import ExportChart from '@/_legacy/views/Analytics/components/ExportChart.vue';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import { isEmpty } from 'lodash/fp';

  export default {
    components: {
      Card,
      ExportChart,
      Loader,
    },
    props: {
      chartData: {
        type: Object,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default: null,
      },
    },
    data: () => ({
      chartist: null,
    }),
    computed: {
      chartDataWithZeroesRemoved() {
        return {
          ...this.chartData,
          series: this.chartData.series.filter(item => item.value > 0),
        };
      },
    },
    watch: {
      chartData:
        {
          handler() {
            if (this.chartist) {
              this.destroyChart();
            }
            if (!isEmpty(this.chartData) && !this.loading) {
              this.drawChart();
            }
          },
          immediate: false,
        },
    },
    mounted() {
      if (!isEmpty(this.chartData) && !this.loading) {
        this.drawChart();
      }
    },
    destroyed() {
      this.destroyChart();
    },
    methods: {
      handleChartClick({ target }) {
        if (target.tagName === 'path' && target.classList.contains('ct-slice-pie')) {
          this.$emit('chartClick', Chartist.deserialize(target.getAttribute('ct:meta')));
        }
      },
      destroyChart() {
        if (this.chartist) {
          this.chartist.detach();
        }

        this.chartist = null;
      },
      drawChart() {
        const total = this.chartData.series.map(item => item.value)
          .reduce((a, b) => a + b, 0);

        this.chartist = new Chartist.Pie(
          this.$refs.chart,
          this.chartDataWithZeroesRemoved,
          {
            labelInterpolationFnc: value => this.$n(value / total, 'analyticsPercentage')
              .replace(' %', '%'),
          },
        );
        this.chartist.supportsForeignObject = false; // this is needed to make png export possible
      },
      handleExport(format) {
        switch (format) {
          case 'png':
            this.downloadPng();
            break;
          // no default
        }
      },
      downloadPng() {
        domtoimage.toPng(this.$refs.chartWithLegend)
          .then(dataUrl => {
            const img = new Image();
            img.src = dataUrl;
            const link = document.createElement('a');
            link.download = 'chart.png';
            link.href = dataUrl;
            link.click();
          });
      },
    },
  };
</script>
